import React, { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import message from '../components/Brand/brandMessage';
import { LoginIcon } from '../components/Brand/Icon';
import url from '../utils/url';
import { getGuestTrialSnapshot, prepareGuestAuth } from './store';

export function useGuestTrialNotice(storyId, onSignIn) {
  const { t } = useTranslation('brand');
  const navigate = useNavigate();
  const notice = useRef(null);
  // The composer flushes its latest text and checks persistence before leaving.
  // Keep that guard current without reopening the notice on every keystroke.
  const signIn = useRef(onSignIn);
  signIn.current = onSignIn;
  const closeNotice = useCallback(() => {
    notice.current?.close();
    notice.current = null;
  }, []);

  useEffect(() => closeNotice, [closeNotice, storyId]);

  const startGuestAuth = useCallback(() => {
    closeNotice();
    if (signIn.current) return signIn.current();
    const returnPath = prepareGuestAuth(storyId, url(`/news/${storyId}/opinion`));
    navigate(`${url('/login')}?from=${encodeURIComponent(returnPath)}`);
  }, [closeNotice, navigate, storyId]);

  const showNotice = useCallback(({ limitReached = false, storageAvailable } = {}) => {
    const persistent = storageAvailable ?? getGuestTrialSnapshot().storageAvailable;
    notice.current = message.info({
      content: [t('guestTrialNotice'), ...(!persistent ? [t('guestTrialMemoryOnly')] : []),
        ...(limitReached ? [t('guestTrialLimitReached')] : [])].join(' '),
      duration: 8,
      action: { label: t('guestSignInAction'), icon: <LoginIcon />, onClick: startGuestAuth },
    });
  }, [startGuestAuth, t]);

  return { showNotice, startGuestAuth, closeNotice };
}
